// Journey state — multi-listing.
//
// Each seller can have multiple in-flight listings. Each lives under a UUID
// in a single localStorage map. `currentId` points at the one being worked on.
//
//   sz_seller_listings   = { [id]: journey, ... }
//   sz_seller_current_id = "abc-123" | null
//
// Stage IDs are dynamic — no STAGES import here, keeping this lib decoupled.

export const LISTINGS_KEY    = "sz_seller_listings";
export const CURRENT_KEY     = "sz_seller_current_id";
const OLD_SINGLE_JOURNEY_KEY = "sz_seller_journey"; // pre-multi-listing

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);

export const blankJourney = (overrides = {}) => ({
  id: newId(),
  address: "",
  property: null,
  currentStageIdx: 0,
  sessionId: null,
  verification: null, // { owner_of_record, is_self, relationship, confirmed_at }
  stages: {},
  created_at: new Date().toISOString(),
  last_opened_at: new Date().toISOString(),
  ...overrides,
});

// ─── Storage ─────────────────────────────────────────────────────────────────
export const loadAllListings = () => {
  try { return JSON.parse(localStorage.getItem(LISTINGS_KEY) || "{}"); }
  catch { return {}; }
};
export const saveAllListings = (m) => {
  try { localStorage.setItem(LISTINGS_KEY, JSON.stringify(m)); } catch {}
};
export const loadJourney = (id) => {
  if (!id) return null;
  return loadAllListings()[id] || null;
};
export const saveJourney = (j) => {
  if (!j?.id) return;
  const all = loadAllListings();
  all[j.id] = { ...j, last_opened_at: new Date().toISOString() };
  saveAllListings(all);
};
export const deleteJourney = (id) => {
  const all = loadAllListings();
  delete all[id];
  saveAllListings(all);
  if (getCurrentId() === id) setCurrentId(null);
};
// All journeys, most-recently-opened first.
export const listJourneys = () =>
  Object.values(loadAllListings())
    .sort((a, b) => new Date(b.last_opened_at || 0) - new Date(a.last_opened_at || 0));

export const getCurrentId = () => {
  try { return localStorage.getItem(CURRENT_KEY) || null; } catch { return null; }
};
export const setCurrentId = (id) => {
  try {
    if (id) localStorage.setItem(CURRENT_KEY, id);
    else localStorage.removeItem(CURRENT_KEY);
  } catch {}
};

// ─── Stage state helpers (unchanged) ────────────────────────────────────────
export const getStageState = (journey, id) =>
  journey?.stages?.[id] || { status: "pending", data: null };

export const patchStage = (journey, id, patch) => ({
  ...journey,
  stages: {
    ...journey.stages,
    [id]: { ...getStageState(journey, id), ...patch },
  },
});

// ─── Migration: old single-journey key → multi-listing map ──────────────────
// Runs once at module load so existing beta sellers don't lose state.
(function migrateOld() {
  if (typeof localStorage === "undefined") return;
  try {
    const raw = localStorage.getItem(OLD_SINGLE_JOURNEY_KEY);
    if (!raw) return;
    const j = JSON.parse(raw);
    if (j?.address) {
      const migrated = { ...blankJourney(), ...j, id: j.id || newId() };
      const all = loadAllListings();
      if (!all[migrated.id]) {
        all[migrated.id] = migrated;
        saveAllListings(all);
        setCurrentId(migrated.id);
      }
    }
  } catch {}
  try { localStorage.removeItem(OLD_SINGLE_JOURNEY_KEY); } catch {}
})();
